"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";
import {
  CircleUser,
  Library,
  Menu,
  Search,
  ShoppingCart,
  Store,
} from "lucide-react";
import { BRAND_NAME, VISIBLE_LOGO_SRC } from "@/lib/brand";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import { marketingNavigation } from "@/content/clawagora-marketing";
import SiteHeaderAuth from "@/components/marketing/site-header-auth";

const marketplaceNavigation = [
  { title: "Guides", href: "/marketplace", icon: Store },
  { title: "My Library", href: "/my-library", icon: Library },
  { title: "Purchases", href: "/my-orders", icon: ShoppingCart },
  { title: "Profile", href: "/profile", icon: CircleUser },
];

export default function SiteHeader() {
  const pathname = usePathname() || "/";
  const isMarketplace =
    pathname.includes("/marketplace") ||
    pathname.startsWith("/my-library") ||
    pathname.startsWith("/my-orders") ||
    pathname.startsWith("/profile");
  const activeMarketplaceHref = pathname.startsWith("/my-library")
    ? "/my-library"
    : pathname.startsWith("/my-orders")
      ? "/my-orders"
      : pathname.startsWith("/profile")
        ? "/profile"
        : "/marketplace";

  if (isMarketplace) {
    return (
      <header className="sticky top-0 z-40 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6">
            <Link href="/" className="flex items-center gap-2">
              <Image
                src={VISIBLE_LOGO_SRC}
                alt={BRAND_NAME}
                width={32}
                height={32}
                sizes="32px"
                className="rounded-lg"
                data-brand-logo="header"
                priority
              />
              <span className="text-xl font-bold text-foreground">{BRAND_NAME}</span>
            </Link>

            <nav className="hidden items-center gap-1 md:flex">
              {marketplaceNavigation.map((item) => {
                const Icon = item.icon;
                const isActive = activeMarketplaceHref === item.href;

                return (
                  <Link
                    key={item.href}
                    href={item.href as any}
                    className={cn(
                      "flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors",
                      isActive
                        ? "bg-primary/10 font-medium text-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {item.title}
                  </Link>
                );
              })}
              <span className="mx-1 h-4 w-px bg-border/50" />
              <Link
                href="/features"
                className="rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                Features
              </Link>
              <Link
                href="/blog"
                className="rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                Notes
              </Link>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <Link href="/marketplace">
              <button
                type="button"
                className="flex h-9 items-center gap-2 rounded-lg border border-border/50 bg-muted/50 px-3 text-sm text-muted-foreground transition-colors hover:bg-muted"
                aria-label="Search"
              >
                <Search className="h-4 w-4" />
                <span className="hidden sm:inline">Archive overview</span>
              </button>
            </Link>
            <button
              type="button"
              className="rounded-lg border border-border/50 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              中文
            </button>
            <SiteHeaderAuth className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90" />
            <button
              type="button"
              className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground md:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="fixed left-0 right-0 top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link href="/" className="flex items-center gap-2">
          <Image
            src={VISIBLE_LOGO_SRC}
            alt={BRAND_NAME}
            width={32}
            height={32}
            sizes="32px"
            className="rounded-lg"
            data-brand-logo="header"
            priority
          />
          <span className="text-xl font-bold text-foreground">{BRAND_NAME}</span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {marketingNavigation.map((item) => (
            <Link
              key={item.href}
              href={item.href as any}
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {item.title}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <button
            type="button"
            className="rounded-lg border border-border/50 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            中文
          </button>
          <SiteHeaderAuth className="inline-flex h-9 items-center justify-center rounded-md px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted" />
          {!isMarketplace ? (
            <Link
              href="/marketplace"
              className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Browse Guides
            </Link>
          ) : null}
        </div>

        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground md:hidden"
          aria-label="Toggle menu"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>
    </header>
  );
}
